import React from 'react';

export interface FilterChipOption<T extends string> {
  value: T;
  label: string;
  /** Rendered beside the label — usually how many rows the chip would show. */
  count?: number;
}

interface FilterChipsProps<T extends string> {
  options: FilterChipOption<T>[];
  /** null = nothing selected, i.e. the unfiltered view. */
  value: T | null;
  /**
   * Fires with null when the active chip is clicked again, so the row can
   * always be switched off — a filter with no way back reads as a broken list.
   */
  onChange: (value: T | null) => void;
  /** Describes the row for screen readers, e.g. "Filter umur tunggakan". */
  ariaLabel?: string;
  className?: string;
}

/**
 * A single-select row of filter chips: pick one, click it again to clear.
 *
 * Single-select because the filters behind it are single-valued server-side;
 * a multi-select row would promise a request the API cannot take.
 */
const FilterChips = <T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className = '',
}: FilterChipsProps<T>) => (
  <div
    role="group"
    aria-label={ariaLabel}
    className={`flex flex-wrap items-center gap-2 ${className}`}
  >
    {options.map((option) => {
      const isActive = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          aria-pressed={isActive}
          onClick={() => onChange(isActive ? null : option.value)}
          className={`rounded-full border px-3 py-1 text-[13px] font-medium transition-colors ${
            isActive
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          {option.label}
          {option.count !== undefined ? (
            <span
              className={`ml-1.5 ${isActive ? 'text-primary' : 'text-gray-400'}`}
            >
              {option.count}
            </span>
          ) : null}
        </button>
      );
    })}
  </div>
);

export default FilterChips;
